import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronUp, ChevronDown } from 'lucide-react';
import CinematicHero from '@/components/sections/CinematicHero';
import FeatureBar from '@/components/sections/FeatureBar';
import CategoryStrip from '@/components/sections/CategoryStrip';
import RangeShowcase from '@/components/sections/RangeShowcase';
import ProductCard from '@/components/ui/ProductCard';
import Bottle from '@/components/ui/Bottle';
import { PRODUCTS } from '@/data/products';

export default function Home() {
  const bestseller = PRODUCTS.find((p) => p.bestseller) || PRODUCTS[0];
  const featured = PRODUCTS.slice(0, 4);

  return (
    <div data-testid="page-home">
      <CinematicHero />
      <FeatureBar />
      <CategoryStrip />
      <RangeShowcase />

      {/* Bestseller spotlight (echoes "Our Bestseller" pane in ref) */}
      <section className="container-x py-20" data-testid="bestseller-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-3 space-y-5">
            <span className="eyebrow">Our Bestseller</span>
            <h2 className="font-display text-5xl lg:text-6xl text-bone-50 leading-[1.02]">
              {bestseller.name.split(' ')[0]} <br /><span className="italic text-sage-300">{bestseller.name.split(' ').slice(1).join(' ')}.</span>
            </h2>
            <p className="text-bone-200/85 leading-relaxed">{bestseller.description}</p>
            <p className="font-display text-3xl text-gold">₹{bestseller.price}</p>
            <Link to={`/shop/${bestseller.id}`} className="btn-primary inline-flex" data-testid="bestseller-cta">
              Add To Cart <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* center stage */}
          <div className="lg:col-span-6 relative bottle-stage rounded-3xl py-10 lg:py-16 grain">
            <div className="absolute left-6 top-1/2 -translate-y-1/2 flex flex-col items-center gap-3 text-bone-300/70">
              <button className="btn-ghost-icon !w-8 !h-8"><ChevronUp className="w-3.5 h-3.5" /></button>
              <div className="w-12 h-16 rounded-xl border border-bone-100/15 bg-white/[0.03] flex items-center justify-center">
                <div className="scale-[0.35] origin-center"><Bottle product={bestseller} size="sm" /></div>
              </div>
              <div className="w-12 h-16 rounded-xl border border-gold/40 bg-gold/[0.06] flex items-center justify-center">
                <div className="scale-[0.35] origin-center"><Bottle product={PRODUCTS[1]} size="sm" /></div>
              </div>
              <button className="btn-ghost-icon !w-8 !h-8"><ChevronDown className="w-3.5 h-3.5" /></button>
            </div>
            <div className="flex justify-center"><Bottle product={bestseller} size="xl" floating /></div>
            <div className="absolute inset-x-0 bottom-6 flex justify-center gap-6 text-[10px] tracking-[0.22em] uppercase text-bone-300/70">
              <span>Drag to rotate</span>
              <span>Scroll to zoom</span>
            </div>
          </div>

          {/* right callouts */}
          <div className="lg:col-span-3 space-y-3">
            {[
              { label: 'Real Ingredients' },
              { label: 'No Added Sugar' },
              { label: 'Refrigerated & Fresh' },
              { label: 'Made With Love' },
            ].map((c, i) => (
              <div key={i} className="glass-soft rounded-full px-5 py-3 flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-gold" />
                <span className="text-sm text-bone-100">{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products grid */}
      <section className="container-x py-16" data-testid="featured-section">
        <div className="flex items-end justify-between mb-10 gap-6">
          <div>
            <span className="eyebrow">Most Loved</span>
            <h2 className="font-display text-4xl lg:text-5xl text-bone-50 mt-3">
              From our shelf to <span className="italic text-sage-300">your hand</span>.
            </h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex btn-outline !py-2.5 !px-5">
            Shop All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Story teaser */}
      <section className="container-x py-20" data-testid="story-teaser">
        <div className="glass-card rounded-3xl p-10 lg:p-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center overflow-hidden relative">
          <div className="absolute -right-32 -top-20 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
          <div className="space-y-5 relative">
            <span className="eyebrow">Our Story</span>
            <h2 className="font-display text-4xl lg:text-5xl text-bone-50">
              A kitchen experiment.<br />
              <span className="italic text-sage-300">An honest theory.</span>
            </h2>
            <p className="text-bone-200/90 leading-relaxed max-w-lg">
              Sip Theory began with a simple belief — that what&apos;s good for you should also taste extraordinary.
              No shortcuts. No artificial sweeteners. Just real fruit, slow craft, and refrigeration to keep it honest.
            </p>
            <Link to="/our-story" className="btn-outline inline-flex" data-testid="story-cta">
              Read the journey <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative flex justify-center items-end gap-3">
            {PRODUCTS.slice(0, 3).map((p, i) => (
              <div key={p.id} className={i === 1 ? '-translate-y-6' : ''}>
                <Bottle product={p} size="md" floating />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
